import React from "react";
import { toast } from "sonner";
import { ShieldAlert, Copy, CheckCircle2 } from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { ASSETS } from "@/lib/assets";

function VaultRow({ label, value }) {
  const empty = value == null || value === "";
  const copy = () => { if (!empty) { navigator.clipboard.writeText(value); toast.success("Copied"); } };
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-charcoal">
      <span className="font-mono text-xs uppercase tracking-wider text-dim">{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`font-mono text-xs font-bold truncate ${empty ? "text-danger" : "text-cream"}`}>
          {empty ? "NOT DEPLOYED" : value}
        </span>
        {!empty && <button onClick={copy} className="text-neon"><Copy className="w-3.5 h-3.5" /></button>}
      </div>
    </div>
  );
}

export default function Vault() {
  const { data } = useSettings();
  const chains = data?.chains || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <span className="tag">Proof Over Promises</span>
      <h1 className="headline text-cream text-4xl sm:text-6xl mt-5">THE <span className="text-neon">VAULT</span></h1>
      <p className="font-mono text-dim mt-4 max-w-2xl">
        Radical transparency. Every official address lives here once verified. Until then, it stays honest: nothing deployed.
      </p>

      <div className="comic-card p-5 mt-8 bg-[#220000] border-danger flex items-start gap-4" data-testid="vault-warning">
        <ShieldAlert className="w-8 h-8 text-danger shrink-0" />
        <p className="font-mono text-sm text-cream/90">
          Never trust addresses posted in replies or DMs. Verify the official address here before interacting.
          There is <span className="text-danger font-bold">no live $ROBBED contract or mint yet.</span>
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        {chains.map((c) => {
          const isSol = c.chainType === "solana";
          return (
            <div key={c.chainName} className="comic-card p-6" data-testid={`vault-${c.chainName.toLowerCase().replace(/\s+/g, "-")}`}>
              <h3 className={`font-display font-900 text-xl uppercase ${isSol ? "text-grape" : "text-neon"}`}>{c.chainName}</h3>
              <div className="mt-4">
                <VaultRow label={isSol ? "Mint Authority" : "Smart Contract"} value={isSol ? c.mintAddress : c.tokenAddress} />
                <VaultRow label="Decimals" value={c.decimals} />
                <VaultRow label="Liquidity Pool" value={c.poolAddress} />
                <VaultRow label="Treasury Wallet" value={c.treasuryAddress} />
                <VaultRow label="Rewards Wallet" value={c.rewardsAddress} />
                <VaultRow label="Team Wallet" value={c.teamAddress} />
                <VaultRow label="Explorer" value={c.explorerUrl} />
                <VaultRow label="Verified" value={c.officialContractVerified ? "YES" : null} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="comic-card p-6 sm:p-8 mt-8 grid md:grid-cols-2 gap-6 items-center">
        <div>
          <h3 className="font-display font-900 text-cream text-xl uppercase">Liquidity & Treasury</h3>
          <p className="font-mono text-sm text-dim mt-3 leading-relaxed">
            Liquidity details will be published before token launch. We'll never claim liquidity is locked or that a
            multisig exists until it's verifiably true.
          </p>
          <div className="flex items-center gap-2 mt-4 font-mono text-xs text-neon">
            <CheckCircle2 className="w-4 h-4" /> No fake data policy enforced.
          </div>
        </div>
        <img src={ASSETS.emblem} alt="ROBBED emblem" className="w-56 justify-self-center rounded-full border-3 border-black" />
      </div>
    </div>
  );
}
